// src/pages/LoginPage.js
import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, FormCheck, Badge, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/api';
import { setUser } from '../redux/slices/userSlice';

const LoginPage = () => {
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      setError('You must accept the Terms of Use and Privacy Policy to continue.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const path = isSystemAdmin ? '/token' : `/tenant/${slug}/token`;
      const response = await api.post(path, new URLSearchParams({ username: email, password }));
      
      const { access_token } = response.data;
      localStorage.setItem('access_token', access_token);

      // Decode payload to check roles
      const payload = JSON.parse(atob(access_token.split('.')[1]));

      // ✅ Update Redux with User Data AND Studio Context
      dispatch(setUser({ 
        ...payload,
        email, 
        tenant_slug: isSystemAdmin ? null : slug,
        app_context: 'studio' // Explicitly set for Studio Portal
      }));

      // ✅ Role-based Navigation logic
      if (payload.role === 'system_admin') {
        navigate('/system-admin');
      } else if (payload.role === 'tenant_admin') {
        navigate(`/tenant/${slug}/dashboard`); // Send directly to Tenant Admin view
      } else {
        navigate('/dashboard'); // Standard SME/QA/Media view
      }
      
    } catch (err) {
      setError('Invalid credentials or tenant slug. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="d-flex align-items-center justify-content-center bg-light" style={{ minHeight: '100vh' }}>
      <Card className="shadow-lg border-0" style={{ width: '32rem', borderRadius: '15px' }}>
        <div className="bg-primary" style={{ height: '5px', borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}></div>
        <Card.Body className="p-5">
          <div className="text-center mb-4">
            <div className="d-flex align-items-center justify-content-center mb-2">
              <img src="/assets/images/logo.png" alt="AcademicOS Logo" height="100" className="me-2" />
              <div className="text-start">
                <span className="fw-bold text-primary h4 mb-0" style={{ letterSpacing: '1px' }}>
                  Academic<span style={{ color: "#B8962E" }}>OS</span>
                </span>
                <small className="d-block text-muted fw-bold" style={{ fontSize: '0.7rem' }}>
                  Embracing Academic Intelligence
                </small>
              </div>
            </div>
            <Badge bg="light" text="dark" className="border">Human-in-the-Loop Governance. Studio Portal</Badge>
          </div>

          {error && <Alert variant="danger" className="small" role="alert">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4 text-center">
              <FormCheck 
                type="switch"
                id="system-admin-switch"
                label="System Admin Portal"
                className="d-inline-block fw-bold text-muted"
                checked={isSystemAdmin}
                onChange={(e) => setIsSystemAdmin(e.target.checked)}
              />
            </Form.Group>

            {!isSystemAdmin && (
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-uppercase text-muted">University Slug</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., tau-gy"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  required
                  disabled={loading}
                  className="bg-light border-0 p-2"
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold text-uppercase text-muted">Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="name@university.edu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="bg-light border-0 p-2"
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="small fw-bold text-uppercase text-muted">Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="bg-light border-0 p-2"
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Check 
                type="checkbox"
                id="terms-checkbox"
                required
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                label={
                  <span className="small text-muted">
                    I agree to the <Link to="/terms" className="text-primary text-decoration-none fw-bold">Terms of Use</Link> and <Link to="/privacy" className="text-primary text-decoration-none fw-bold">Privacy Policy</Link>.
                  </span>
                }
              />
            </Form.Group>

            <Button variant="primary" type="submit" className="w-100 fw-bold py-2 shadow-sm" disabled={loading}>
              {loading ? <Spinner size="sm" className="me-2" aria-hidden="true" /> : 'Sign In'}
            </Button>
          </Form>

          <hr className="my-4" />
 
          <div className="text-center">
            <p className="text-muted small mb-2">New to AcademicOS?</p>
            <Button variant="outline-primary" size="sm" onClick={() => navigate('/register')} className="px-4">
              Create New Institutional Account
            </Button>
          </div>

          <div className="text-center mt-4 pt-3 border-top">
            <small className="text-muted italic" style={{ fontSize: '0.75rem' }}>
              AI drafts content — Humans review, edit, and approve.
            </small>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default LoginPage;